import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export const ComercialForm = ({ onSuccess, onCancel, statusTypes }) => {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", phone: "", whatsapp: "", email: "",
    city: "", state: "", status: "lead_redes", sales_comments: "", policy_type_id: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [policyTypes, setPolicyTypes] = useState([]);
  const { token } = useAuth();

  useEffect(() => {
    axios.get(`${API}/policy-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPolicyTypes(res.data)).catch(() => {});
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() && !form.contact_name.trim()) {
      toast.error("Ingresa al menos Empresa o Encargado");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/comercial`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contacto creado");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear contacto");
    } finally { setIsLoading(false); }
  };

  const set = (field, value) => setForm({ ...form, [field]: value });

  return (
    <div data-testid="comercial-form">
      <button onClick={onCancel} className="flex items-center gap-1 text-sm text-blue-600 mb-4 hover:underline" data-testid="comercial-back">
        <CaretRight size={18} className="rotate-180" /> Volver
      </button>
      <div className="form-section">
        <h2>Nuevo Contacto Comercial</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nombre de Empresa</Label>
              <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="input-sharp mt-1" placeholder="Empresa S.A. de C.V." data-testid="comercial-company" />
            </div>
            <div>
              <Label>Nombre Encargado</Label>
              <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="input-sharp mt-1" placeholder="Nombre completo" data-testid="comercial-contact" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Celular</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-sharp mt-1" placeholder="55 1234 5678" data-testid="comercial-phone" />
            </div>
            <div>
              <Label>WhatsApp</Label>
              <Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="input-sharp mt-1" placeholder="55 1234 5678" data-testid="comercial-whatsapp" />
            </div>
            <div>
              <Label>Correo</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-sharp mt-1" placeholder="correo@empresa.com" data-testid="comercial-email" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Ciudad</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="input-sharp mt-1" placeholder="Ciudad" data-testid="comercial-city" />
            </div>
            <div>
              <Label>Estado</Label>
              <Input value={form.state} onChange={(e) => set("state", e.target.value)} className="input-sharp mt-1" placeholder="Estado" data-testid="comercial-state" />
            </div>
            <div>
              <Label>Estatus</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="input-sharp mt-1" data-testid="comercial-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(statusTypes || []).map(st => (
                    <SelectItem key={st.name} value={st.name}>{st.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {policyTypes.length > 0 && (
            <div>
              <Label>Poliza</Label>
              <Select value={form.policy_type_id || "sin_poliza"} onValueChange={(v) => set("policy_type_id", v === "sin_poliza" ? "" : v)}>
                <SelectTrigger className="input-sharp mt-1" data-testid="comercial-policy-create">
                  <SelectValue placeholder="Sin poliza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin_poliza">Sin poliza</SelectItem>
                  {policyTypes.map(pt => (
                    <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Notas iniciales</Label>
            <Textarea value={form.sales_comments} onChange={(e) => set("sales_comments", e.target.value)} className="input-sharp mt-1 min-h-[80px]" placeholder="Notas sobre el contacto, interes, seguimiento..." data-testid="comercial-comments" />
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" className="btn-secondary" onClick={onCancel}>Cancelar</Button>
            <Button type="submit" className="btn-primary" disabled={isLoading} data-testid="comercial-submit">
              {isLoading ? "Creando..." : "Crear Contacto"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
